import { put, select, takeEvery } from 'redux-saga/effects';

import { resourceSagaUtils, resourceStoreTypes } from '../core';
import type { ResourceActionCreator, ResourceFailureActionCreator } from '../core/resourceStoreTypes';
import { featureFlagsSelectors } from '../feature-flags';
import * as actions from './securitiesActions';
import { getLastSyncDate } from './securitiesSelectors';
import { transformResponseToResources } from './securitiesTransformers';
import { mkSecurity, type SecurityProps } from './securitiesTypes';

const mkQcsSyncSecuritiesConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<SecurityProps>();

const mkResourceConfig = (
  successAction: ResourceActionCreator<SecurityProps>,
  failureAction: ResourceFailureActionCreator,
) =>
  mkQcsSyncSecuritiesConfig({
    resourceName: 'securities',
    resourceBaseUrl: '/v2/investments/securities',
    getLastSyncDate,
    applyResourceFromCRUDResponse: true,

    transformResponseToResources,

    successAction,
    failureAction,
  });

export function* getSecuritiesActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.getSecuritiesSuccess, actions.getSecuritiesFailure);
  yield resourceSagaUtils.takeAndDebounce(
    actions.getSecurities,
    resourceSagaUtils.qcsSyncGetResources<SecurityProps>,
    resourceConfig,
  );
}

export function* createSecurityActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.createSecuritySuccess, actions.createSecurityFailure);
  yield takeEvery(actions.createSecurity.type, resourceSagaUtils.qcsSyncCreateResource<SecurityProps>, resourceConfig);
}

export function* updateSecurityActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.updateSecuritySuccess, actions.updateSecurityFailure);
  yield takeEvery(actions.updateSecurity.type, resourceSagaUtils.qcsSyncUpdateResource<SecurityProps>, resourceConfig);
}

export function* deleteSecurityActionWatcher() {
  const resourceConfig = mkResourceConfig(actions.deleteSecuritySuccess, actions.deleteSecurityFailure);
  yield takeEvery(actions.deleteSecurity.type, resourceSagaUtils.qcsSyncDeleteResource<SecurityProps>, resourceConfig);
}

const { callFirst, callLast } = resourceSagaUtils.createGreedDebounce('securitiesDumpUpdater');
export function* securitiesDumpUpdater(resources: SecurityProps[]) {
  const bumpSecurities: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpSecurities');
  if (bumpSecurities) {
    yield callFirst(securitiesDumpUpdaterFull, resources);
    yield callLast(securitiesNudgeUpdater);
  }
}

function* securitiesDumpUpdaterFull(resources: Array<SecurityProps>) {
  const securities = resources.map((resource) => mkSecurity(resource));
  yield put(actions.getSecuritiesSuccess({ resources: securities }, { context: 'BUMP_CHANGE_DUMP' }));
}

export function* securitiesNudgeUpdater() {
  const bumpSecurities: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpSecurities');
  if (bumpSecurities) {
    yield put(actions.getSecurities(undefined, { context: 'BUMP_CHANGE_NUDGE' }));
  }
}

export default [
  getSecuritiesActionWatcher,
  createSecurityActionWatcher,
  updateSecurityActionWatcher,
  deleteSecurityActionWatcher,
];
